import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, CheckCircle, PushPin, Trophy, Coins } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * SignalsBell — a header dropdown showing recent Signals.
 *
 * Polls `/signals/unread-count` every 45s for the badge, and lazy-loads the
 * feed the first time the dropdown opens.
 */
export default function SignalsBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [feed, setFeed] = useState(null);
  const [unread, setUnread] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const tick = () => {
      api.get("/signals/unread-count")
         .then((r) => { if (alive) setUnread(r.data?.unread ?? 0); })
         .catch(() => {});
    };
    tick();
    const id = setInterval(tick, 45000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  useEffect(() => {
    function onClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    if (open && !feed) {
      api.get("/signals?limit=8").then((r) => setFeed(r.data)).catch(() => {});
    }
  }, [open, feed]);

  if (!user) return null;

  const markRead = async (sid) => {
    if (!sid || sid.startsWith("auto_")) return;
    try {
      await api.post(`/signals/${sid}/read`);
      setUnread((n) => Math.max(0, n - 1));
      setFeed((f) => f ? {
        ...f,
        items: f.items.map((it) => it.signal_id === sid ? { ...it, read: true } : it),
      } : f);
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await api.post("/signals/mark-all-read");
      setUnread(0);
      setFeed((f) => f ? {
        ...f,
        items: f.items.map((it) => ({ ...it, read: true })),
      } : f);
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={boxRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="signals-bell-button"
        aria-label={`Signals${unread ? ` (${unread} unread)` : ""}`}
        className="relative w-9 h-9 rounded-md hover:bg-white/8 flex items-center justify-center text-white/70 hover:text-white transition-colors"
      >
        <Bell size={18} weight={unread ? "fill" : "duotone"} />
        {unread > 0 && (
          <span
            data-testid="signals-bell-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-nk-gold text-nk-charcoal text-[9px] font-mono font-bold flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="signals-bell-dropdown"
          className="absolute right-0 top-11 w-[360px] max-w-[92vw] rounded-md border border-white/12 bg-nk-charcoal shadow-2xl z-40 overflow-hidden animate-fade-up"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <p className="font-display text-sm text-white">Signals</p>
            <button
              onClick={markAllRead}
              disabled={unread === 0}
              data-testid="signals-mark-all-read"
              className="text-[10px] uppercase tracking-widest text-nk-gold hover:text-nk-gold/80 disabled:text-white/25 disabled:cursor-not-allowed"
            >
              Mark all read
            </button>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {!feed ? (
              <div className="px-4 py-8 text-center text-[11px] text-white/40 font-mono">Loading…</div>
            ) : feed.items.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell size={28} weight="duotone" className="text-white/25 mx-auto mb-2" />
                <p className="text-xs text-white/50">No signals yet</p>
                <p className="text-[10px] text-white/35 mt-1">Check back once championships and prizes are live.</p>
              </div>
            ) : (
              feed.items.map((it) => <SignalRow key={it.signal_id} item={it} onRead={markRead} />)
            )}
          </div>

          <Link
            to="/signals"
            onClick={() => setOpen(false)}
            data-testid="signals-see-all-link"
            className="block px-4 py-2.5 text-center text-[11px] uppercase tracking-widest text-white/60 hover:text-nk-gold border-t border-white/10 hover:bg-white/[0.03] transition-colors"
          >
            See all signals →
          </Link>
        </div>
      )}
    </div>
  );
}

const TYPE_ICONS = {
  announcement: Bell,
  championship: Trophy,
  prize: Coins,
  update: Bell,
  event: Bell,
  mode: Bell,
};

function SignalRow({ item, onRead }) {
  const Icon = TYPE_ICONS[item.signal_type] || Bell;
  const isPrizeOrChamp = item.signal_type === "prize" || item.signal_type === "championship";
  const isAuto = item.signal_id?.startsWith?.("auto_");
  const detailHref = isAuto ? (item.cta_href || "/signals") : `/signals/${item.signal_id}`;

  const inner = (
    <div className="flex gap-3 items-start">
      <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 mt-0.5 ${
        item.read ? "bg-white/4 text-white/45" : "bg-nk-gold/15 text-nk-gold"
      }`}>
        <Icon size={14} weight={item.read ? "duotone" : "fill"} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          {item.pinned && <PushPin size={10} weight="fill" className="text-nk-gold shrink-0" />}
          <p className={`text-sm font-medium leading-tight truncate ${
            item.read ? "text-white/70" : "text-white"
          }`}>{item.title}</p>
        </div>
        <p className="text-[11px] text-white/55 line-clamp-2 leading-snug">
          {item.summary || item.body}
        </p>
        {item.cta_label && (
          <span className="inline-block mt-1.5 text-[10px] uppercase tracking-widest text-nk-gold">
            {item.cta_label} →
          </span>
        )}
      </div>
      {!item.read && !isPrizeOrChamp && (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRead(item.signal_id); }}
          className="text-white/30 hover:text-nk-gold mt-1 shrink-0"
          aria-label="Mark read"
          data-testid={`signal-mark-read-${item.signal_id}`}
        >
          <CheckCircle size={14} weight="regular" />
        </button>
      )}
    </div>
  );

  const wrapperClass = `px-4 py-3 border-b border-white/6 last:border-b-0 transition-colors ${
    item.read ? "" : "bg-white/[0.02]"
  } hover:bg-white/[0.05]`;

  return (
    <Link
      to={detailHref}
      onClick={() => onRead(item.signal_id)}
      data-testid={`signal-row-${item.signal_id}`}
      className={`block ${wrapperClass}`}
    >
      {inner}
    </Link>
  );
}
